import {
  Button,
  Emphasis,
  FormAction,
  FormField,
  FormGrid,
  FormNotice,
  FormRow,
  FormTitle,
} from "@lya-protect/lya-protect-form-library/dist/UI";
import "./consent.scss";
import {
  Form,
  InputCheckbox,
  useFormConfig,
} from "@lya-protect/lya-protect-form-library/dist/FormAPI";
import { zod } from "@lya-protect/lya-protect-form-library/dist/Exports";
import { NavButton } from "@src/components/navButton/NavButton";
import { Stepbar } from "@src/components/stepbar/Stepbar";
import { environment } from "@src/MarqueBlanche";
import { MicroStep, StepProps } from "@src/Steps/step/Steps";

export const CONSENT_DEMATERIALISE =
  "J’accepte de recevoir chacun des documents sous format dématérialisé, le processus de souscription étant entièrement digitalisé ";
export const CONSENTEMENT_AVERTISSEMENT =
  "Vous reconnaissez avoir pris connaissance des avertissements suivants : - Les éléments que vous avez renseignés sont complets, sincères et exacts  - Vous reconnaissez avoir reçu une information sur l&apos;étendue, la définition des risques et des garanties proposées à travers le document d'information normalisé sur le produit d'assurance « DIPA » et le « Règlement » pavixie y compris ses annexes. - Vous vous engagez à informer Tutélaire de toute modification concernant votre situation personnelle - Vous reconnaissez avoir été informé(e) que Tutélaire est susceptible de ne pas vous fournir l&apos;intégralité des services qu'il propose dans le cas où vous refuseriez de compléter tout ou partie du présent document - Par ailleurs, vous reconnaissez avoir été informé par Tutélaire qu'une fausse déclaration ou réticence peut entraîner la nullité de votre contrat (L. 221-14 du code de la mutualité) ou la majoration de la cotisation (L. 221-15 du code de la mutualité)";

const schema = zod.object({
  consentSincere: zod
    .boolean({ required_error: "Champ requis" })
    .refine((v) => v, "Champ requis"),
  consentRisque: zod
    .boolean({ required_error: "Champ requis" })
    .refine((v) => v, "Champ requis"),
  consentModify: zod
    .boolean({ required_error: "Champ requis" })
    .refine((v) => v, "Champ requis"),
  consentNoComplete: zod
    .boolean({ required_error: "Champ requis" })
    .refine((v) => v, "Champ requis"),
  consentLaw: zod
    .boolean({ required_error: "Champ requis" })
    .refine((v) => v, "Champ requis"),
});

export type ConsentData = zod.infer<typeof schema>;

type ConsentProps = StepProps<ConsentData>;

export function Consent({ changeStep, setData, data }: ConsentProps) {
  const formConfig = useFormConfig<typeof schema, ConsentData>({
    schema,
    defaultValues: data,
  });

  const handleSubmit = (submittedData: ConsentData) => {
    setData(submittedData);
    changeStep("AVANT_DE_POURSUIVRE", "forward");
  };

  return (
    <div className="consent-step">
      <div className="stepper__centered-container">
        <div className="stepper__centered-content step__grid step__grid--padding-bottom">
          <Stepbar activeMicroStep={MicroStep.PersonalInformation} />
          <NavButton
            onClick={() => {
              changeStep("RECAP_SOUSCRIPTION", "backward");
            }}
          >
            Précédent
          </NavButton>
          <Form
            className="consent-step__form"
            formConfig={formConfig}
            onSubmit={handleSubmit}
          >
            <FormGrid>
              <FormTitle>
                <h1>
                  Merci de prendre connaissance des avertissements suivants afin
                  de finaliser la complétude de la Fiche d&apos;informations et
                  de conseil :
                </h1>
              </FormTitle>
              <FormNotice>
                <p>
                  <Emphasis>*</Emphasis> Mentions obligatoires
                </p>
              </FormNotice>
              <FormRow>
                <FormField>
                  <InputCheckbox<ConsentData>
                    name="consentSincere"
                    required
                    label="Les éléments que vous avez renseignés sont complets, sincères et exacts"
                  />
                </FormField>
              </FormRow>
              <FormRow>
                <FormField>
                  <InputCheckbox<ConsentData>
                    name="consentRisque"
                    required
                    label={
                      <>
                        Vous reconnaissez avoir reçu une information sur
                        l&apos;étendue, la définition des risques et des
                        garanties proposées à travers le{" "}
                        <a
                          tabIndex={-1}
                          target="_blank"
                          href={environment.current.docDIPA}
                          rel="noreferrer"
                        >
                          document d&apos;information sur le produit
                          d&apos;assurance
                        </a>{" "}
                        « DIPA » et le «{" "}
                        <a
                          tabIndex={-1}
                          target="_blank"
                          href={environment.current.docReglement}
                          rel="noreferrer"
                        >
                          Règlement
                        </a>{" "}
                        » Pavixie y compris ses annexes.
                      </>
                    }
                  />
                </FormField>
              </FormRow>
              <FormRow>
                <FormField>
                  <InputCheckbox<ConsentData>
                    name="consentModify"
                    required
                    label="Vous vous engagez à informer Tutélaire de toute modification concernant votre situation personnelle"
                  />
                </FormField>
              </FormRow>
              <FormRow>
                <FormField>
                  <InputCheckbox<ConsentData>
                    name="consentNoComplete"
                    required
                    label="Vous reconnaissez avoir été informé(e) que Tutélaire
                            est susceptible de ne pas vous fournir
                            l'intégralité des services qu'il propose
                            dans le cas où vous refuseriez de compléter tout ou
                            partie du présent document"
                  />
                </FormField>
              </FormRow>
              <FormRow>
                <FormField>
                  <InputCheckbox<ConsentData>
                    name="consentLaw"
                    required
                    label={
                      <>
                        Par ailleurs, vous reconnaissez avoir été informé par
                        Tutélaire qu&apos;une fausse déclaration ou réticence
                        peut entraîner la nullité de votre contrat (L. 221-14 du
                        code de la mutualité) ou la majoration de la cotisation{" "}
                        <br />
                        (L. 221-15 du code de la mutualité)
                      </>
                    }
                  />
                </FormField>
              </FormRow>
              <FormAction>
                <Button
                  type="submit"
                  disabled={
                    !formConfig.formState.isValid ||
                    formConfig.formState.isSubmitting ||
                    formConfig.formState.isSubmitSuccessful
                  }
                >
                  Suivant
                </Button>
              </FormAction>
            </FormGrid>
          </Form>
        </div>
      </div>
    </div>
  );
}
